import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { 
  Award, LogOut, FileText, CheckCircle, Clock, 
  ChevronDown, ChevronUp, Star, Trophy, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);
  const [scoreDialog, setScoreDialog] = useState({ open: false, application: null });
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const openScoreDialog = async (app) => {
    try {
      const response = await axios.get(`${API}/applications/${app.id}`);
      const fullApp = response.data;
      setScoreDialog({ open: true, application: fullApp });
      
      // Initialize scores based on scholarship criteria
      const initialScores = {};
      fullApp.scholarship?.scoring_criteria?.forEach(criterion => {
        // Check if user already scored
        const existingScore = fullApp.scores?.find(s => s.scorer_id === user.id);
        initialScores[criterion.name] = existingScore?.scores?.[criterion.name] || 5;
      });
      setScores(initialScores);
      setComments('');
    } catch (error) {
      toast.error('Failed to load application details');
    }
  };

  const submitScore = async () => {
    try {
      await axios.post(`${API}/applications/${scoreDialog.application.id}/score`, {
        application_id: scoreDialog.application.id,
        scores: scores,
        comments: comments
      });
      toast.success('Score submitted successfully!');
      setScoreDialog({ open: false, application: null });
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit score');
    }
  };

  const awardScholarship = async (applicationId) => {
    try {
      await axios.post(`${API}/applications/${applicationId}/award`);
      toast.success('Scholarship awarded!');
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to award');
    }
  };

  const rejectApplication = async (applicationId) => {
    try {
      await axios.post(`${API}/applications/${applicationId}/reject`);
      toast.success('Application rejected');
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const pendingApps = applications.filter(a => a.status === 'submitted' || a.status === 'under_review');
  const awardedApps = applications.filter(a => a.status === 'awarded');
  const rejectedApps = applications.filter(a => a.status === 'rejected');

  const hasUserScored = (app) => {
    return app.scores?.some(s => s.scorer_id === user.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-greek-blue rounded-sm flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-greek-blue">AHEPA Family</h1>
              <p className="text-xs text-slate-500">Review Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-sm border-slate-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            Application Review
          </h2>
          <p className="text-slate-600">
            Review, score, and manage scholarship applications.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-white border border-slate-200 p-1 rounded-sm">
            <TabsTrigger value="pending" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-pending">
              Pending Review ({pendingApps.length})
            </TabsTrigger>
            <TabsTrigger value="awarded" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-awarded">
              Awarded ({awardedApps.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-rejected">
              Not Selected ({rejectedApps.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {pendingApps.length === 0 ? (
              <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">No Pending Applications</h3>
                <p className="text-slate-500">All applications have been reviewed.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingApps.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    expanded={expandedApp === app.id}
                    onToggle={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                    onScore={() => openScoreDialog(app)}
                    onAward={() => awardScholarship(app.id)}
                    onReject={() => rejectApplication(app.id)}
                    hasScored={hasUserScored(app)}
                    isChair={user?.role === 'chair'}
                    userId={user?.id}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="awarded">
            {awardedApps.length === 0 ? (
              <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
                <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">No Awarded Scholarships</h3>
                <p className="text-slate-500">No scholarships have been awarded yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {awardedApps.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    expanded={expandedApp === app.id}
                    onToggle={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                    isAwarded
                    userId={user?.id}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="rejected">
            {rejectedApps.length === 0 ? (
              <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
                <XCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">No Rejected Applications</h3>
                <p className="text-slate-500">No applications have been rejected.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {rejectedApps.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    expanded={expandedApp === app.id}
                    onToggle={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                    isRejected
                    userId={user?.id}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Score Dialog */}
      <Dialog open={scoreDialog.open} onOpenChange={(open) => setScoreDialog({ ...scoreDialog, open })}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Score Application - {scoreDialog.application?.applicant_info?.first_name} {scoreDialog.application?.applicant_info?.family_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {scoreDialog.application?.scholarship?.scoring_criteria?.map((criterion) => (
              <div key={criterion.name} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">{criterion.name}</Label>
                    <p className="text-sm text-slate-500">{criterion.description}</p>
                    <p className="text-xs text-slate-400">Weight: {(criterion.weight * 100).toFixed(0)}%</p>
                  </div>
                  <span className="text-2xl font-bold text-greek-blue">{scores[criterion.name] || 0}</span>
                </div>
                <Slider
                  value={[scores[criterion.name] || 0]}
                  onValueChange={([value]) => setScores(prev => ({ ...prev, [criterion.name]: value }))}
                  max={criterion.max_score}
                  step={1}
                  className="w-full"
                  data-testid={`slider-${criterion.name}`}
                />
                <div className="flex justify-between text-xs text-slate-400">
                  <span>0</span>
                  <span>{criterion.max_score}</span>
                </div>
              </div>
            ))}
            
            <div className="space-y-2 pt-4 border-t">
              <Label>Comments (Optional)</Label>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments about this application..."
                className="rounded-sm min-h-[100px]"
                data-testid="textarea-score-comments"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScoreDialog({ open: false, application: null })} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={submitScore} className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm" data-testid="submit-score-btn">
              <Star className="w-4 h-4 mr-2" />
              Submit Score
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Application Card Component
const ApplicationCard = ({ app, expanded, onToggle, onScore, onAward, onReject, hasScored, isChair, isAwarded, isRejected, userId }) => {
  return (
    <div className="bg-white rounded-sm border border-slate-200 overflow-hidden" data-testid={`review-app-card-${app.id}`}>
      <div className="p-6 cursor-pointer hover:bg-slate-50 transition-colors" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold text-slate-900">
              {app.applicant_info?.first_name} {app.applicant_info?.family_name}
            </h3>
            <p className="text-sm text-slate-500">{app.scholarship_name}</p>
            <p className="text-sm text-slate-400">{app.academic_info?.current_institution} - {app.academic_info?.program}</p>
          </div>
          <div className="flex items-center gap-4">
            {app.final_score !== null && (
              <div className="text-center">
                <p className="text-2xl font-bold text-greek-gold">{app.final_score?.toFixed(1)}</p>
                <p className="text-xs text-slate-500">Avg Score</p>
              </div>
            )}
            {isAwarded && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium bg-emerald-100 text-emerald-700">
                <Trophy className="w-4 h-4" />
                Awarded
              </span>
            )}
            {isRejected && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium bg-red-100 text-red-700">
                <XCircle className="w-4 h-4" />
                Not Selected
              </span>
            )}
            {!isAwarded && !isRejected && (
              <>
                {hasScored ? (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-4 h-4" />
                    Scored
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium bg-amber-100 text-amber-700">
                    <Clock className="w-4 h-4" />
                    Pending
                  </span>
                )}
              </>
            )}
            {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-200 p-6 bg-slate-50">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-semibold text-slate-800 mb-3">Personal Information</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-slate-500">Name:</span> {app.applicant_info?.first_name} {app.applicant_info?.middle_name} {app.applicant_info?.family_name}</p>
                <p><span className="text-slate-500">Email:</span> {app.applicant_info?.email}</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-3">Greek Heritage</h4>
              <p className="text-sm text-slate-600">{app.applicant_info?.greek_heritage || 'Not provided'}</p>
            </div>
          </div>

          {/* Scores */}
          {app.scores && app.scores.length > 0 && (
            <div className="mb-6">
              <h4 className="font-semibold text-slate-800 mb-3">Reviewer Scores</h4>
              <div className="space-y-2">
                {app.scores.map((score, index) => (
                  <div key={index} className="p-3 bg-white rounded-sm border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-slate-700">{score.scorer_name} ({score.scorer_role})</span>
                      <span className="font-bold text-greek-blue">
                        {Object.values(score.scores).reduce((a, b) => a + b, 0).toFixed(1)}
                      </span>
                    </div>
                    {score.comments && (
                      <p className="text-sm text-slate-500 italic">"{score.comments}"</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {!isAwarded && !isRejected && (
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button
                onClick={(e) => { e.stopPropagation(); onScore(); }}
                className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
                data-testid={`score-btn-${app.id}`}
              >
                <Star className="w-4 h-4 mr-2" />
                {hasScored ? 'Update Score' : 'Score Application'}
              </Button>
              {isChair && (
                <>
                  <Button
                    onClick={(e) => { e.stopPropagation(); onAward(); }}
                    className="bg-greek-gold hover:bg-greek-gold-dark text-white rounded-sm"
                    data-testid={`award-btn-${app.id}`}
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Award Scholarship
                  </Button>
                  <Button
                    onClick={(e) => { e.stopPropagation(); onReject(); }}
                    variant="outline"
                    className="rounded-sm border-red-300 text-red-600 hover:bg-red-50"
                    data-testid={`reject-btn-${app.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewDashboard;
